import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Package, Gavel, Eye, Clock, CheckCircle, XCircle, AlertCircle,
  Plus, ChevronRight, LogOut, Building, Euro, Users, Award,
  FileText, MapPin, Calendar, ArrowRight, Edit, Trash2, Lock,
  Camera, Upload, Star, X, File, Wrench, Settings
} from 'lucide-react';

export default function EspaceVendeur() {
  const { API, user, logout } = useContext(AppContext);
  const navigate = useNavigate();
  const [mesLots, setMesLots] = useState([]);
  const [vacationsDisponibles, setVacationsDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLot, setSelectedLot] = useState(null);
  const [offresLot, setOffresLot] = useState(null);
  const [showOffresModal, setShowOffresModal] = useState(false);
  const [showCreateLotModal, setShowCreateLotModal] = useState(false);
  const [showAttributionModal, setShowAttributionModal] = useState(false);
  const [attributionData, setAttributionData] = useState({ classement: [], inscriptionRetenue: null, motif: '' });
  const [creatingLot, setCreatingLot] = useState(false);

  // File refs
  const photoInputRef = useRef(null);
  const docInputRef = useRef(null);

  // Form state for new lot with structured fields
  const [newLot, setNewLot] = useState({
    vacation_id: '',
    titre_fr: '',
    description_vendeur_fr: '',
    // Structured fields
    marque: '',
    modele: '',
    annee_mise_service: '',
    heures_compteur: '',
    etat_general: '',
    // Location
    commune: '',
    departement: '',
    // Price
    mise_a_prix: 0,
    pas_enchere: 100,
    mode_visite: 'facultative',
    reserve_aux_pros: true
  });

  // Photos and documents state
  const [selectedPhotos, setSelectedPhotos] = useState([]);  // File objects + previews
  const [selectedDocs, setSelectedDocs] = useState([]);  // File objects
  const [mainPhotoIndex, setMainPhotoIndex] = useState(0);

  useEffect(() => {
    if (!user) {
      navigate('/encheres/connexion');
      return;
    }
    // Check if user has vendor role
    const roles = user.roles || [];
    if (!roles.includes('enchere_vendeur') && !roles.includes('admin')) {
      toast.error('Accès réservé aux vendeurs enchères');
      navigate('/encheres');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [lotsRes, vacationsRes] = await Promise.all([
        axios.get(`${API}/encheres/vendeur/mes-lots?vendeur_id=${user.id}`),
        axios.get(`${API}/encheres/vendeur/vacations-disponibles`)
      ]);
      setMesLots(lotsRes.data || []);
      setVacationsDisponibles(vacationsRes.data || []);
    } catch (error) {
      console.error('Error fetching vendor data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchOffresForLot = async (lot) => {
    try {
      const res = await axios.get(
        `${API}/encheres/vendeur/lots/${lot.id}/offres-vendeur?vendeur_id=${user.id}`
      );
      setOffresLot(res.data);
      setSelectedLot(lot);
      setShowOffresModal(true);
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Erreur lors du chargement des offres');
    }
  };

  const handleCreateLot = async () => {
    if (!newLot.vacation_id || !newLot.titre_fr || !newLot.description_vendeur_fr || !newLot.mise_a_prix) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Validate photos - at least one photo required
    if (selectedPhotos.length === 0) {
      toast.error('Au moins une photo est requise');
      return;
    }

    setCreatingLot(true);
    
    try {
      // Step 1: Create the lot
      const lotData = {
        ...newLot,
        annee_mise_service: newLot.annee_mise_service ? parseInt(newLot.annee_mise_service) : null,
        heures_compteur: newLot.heures_compteur ? parseInt(newLot.heures_compteur) : null,
        etat_general: newLot.etat_general || null
      };
      
      const response = await axios.post(`${API}/encheres/vendeur/lots?vendeur_id=${user.id}`, lotData);
      const lotId = response.data.lot_id;
      
      // Step 2: Upload photos
      if (selectedPhotos.length > 0) {
        const photoFormData = new FormData();
        selectedPhotos.forEach((photo) => {
          photoFormData.append('files', photo.file);
        });
        
        await axios.post(
          `${API}/encheres/vendeur/lots/${lotId}/upload-photos?vendeur_id=${user.id}&main_photo_index=${mainPhotoIndex}`,
          photoFormData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      
      // Step 3: Upload documents
      if (selectedDocs.length > 0) {
        const docFormData = new FormData();
        selectedDocs.forEach((doc) => {
          docFormData.append('files', doc.file);
        });
        
        await axios.post(
          `${API}/encheres/vendeur/lots/${lotId}/upload-documents?vendeur_id=${user.id}&type_doc=fiche_technique`,
          docFormData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      
      toast.success('Lot créé avec succès !');
      setShowCreateLotModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating lot:', error);
      let msg = 'Erreur lors de la création';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(msg);
    } finally {
      setCreatingLot(false);
    }
  };

  const resetForm = () => {
    setNewLot({
      vacation_id: '',
      titre_fr: '',
      description_vendeur_fr: '',
      marque: '',
      modele: '',
      annee_mise_service: '',
      heures_compteur: '',
      etat_general: '',
      commune: '',
      departement: '',
      mise_a_prix: 0,
      pas_enchere: 100,
      mode_visite: 'facultative',
      reserve_aux_pros: true
    });
    setSelectedPhotos([]);
    setSelectedDocs([]);
    setMainPhotoIndex(0);
  };

  // Photo handling
  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setSelectedPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (index) => {
    setSelectedPhotos(prev => {
      const updated = prev.filter((_, i) => i !== index);
      // Adjust main photo index if needed
      if (index === mainPhotoIndex && updated.length > 0) {
        setMainPhotoIndex(0);
      } else if (index < mainPhotoIndex) {
        setMainPhotoIndex(prev => prev - 1);
      }
      return updated;
    });
  };

  // Document handling
  const handleDocSelect = (e) => {
    const files = Array.from(e.target.files);
    const newDocs = files.map(file => ({
      file,
      name: file.name
    }));
    setSelectedDocs(prev => [...prev, ...newDocs]);
  };

  const removeDoc = (index) => {
    setSelectedDocs(prev => prev.filter((_, i) => i !== index));
  };

  const handleAttribution = async () => {
    if (!selectedLot) return;
    try {
      await axios.post(
        `${API}/encheres/vendeur/lots/${selectedLot.id}/attribution?vendeur_id=${user.id}`,
        null,
        {
          params: {
            inscription_id_retenue: attributionData.inscriptionRetenue || null,
            motif: attributionData.motif || null,
            classement: attributionData.classement
          }
        }
      );
      toast.success(attributionData.inscriptionRetenue 
        ? 'Lot attribué avec succès' 
        : 'Décision enregistrée (aucune offre retenue)');
      setShowAttributionModal(false);
      setShowOffresModal(false);
      fetchData();
    } catch (error) {
      console.error('Error attributing lot:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'attribution');
    }
  };

  const getStatusConfig = (statut) => {
    const configs = {
      a_venir: { label: 'À venir', color: 'bg-blue-100 text-blue-700', icon: Clock },
      ouvert: { label: 'Enchères ouvertes', color: 'bg-green-100 text-green-700', icon: Gavel },
      clos: { label: 'Enchères closes', color: 'bg-orange-100 text-orange-700', icon: Lock },
      attribue: { label: 'Attribué', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
      non_attribue: { label: 'Non attribué', color: 'bg-gray-100 text-gray-700', icon: XCircle }
    };
    return configs[statut] || { label: statut, color: 'bg-gray-100 text-gray-700', icon: AlertCircle };
  };

  const formatPrice = (price) => new Intl.NumberFormat('fr-FR').format(price);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#F0A22E]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="espace-vendeur-page">
      <Helmet>
        <title>Espace vendeur | GEOTS Enchères</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <div className="bg-[#0A2540] text-white">
        <div className="container mx-auto px-4 py-8">
          <nav className="flex items-center gap-2 text-sm text-gray-300 mb-4">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/encheres" className="hover:text-white">Enchères</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">Espace vendeur</span>
          </nav>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-orange-500/20 p-3 rounded-lg">
                <Building className="w-8 h-8 text-orange-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold" data-testid="espace-vendeur-title">Espace vendeur</h1>
                <p className="text-gray-300">{user?.company || user?.name}</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-white/10"
              onClick={() => {
                logout();
                navigate('/encheres');
              }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <Package className="w-8 h-8 mx-auto text-[#0A2540] mb-2" />
              <p className="text-2xl font-bold">{mesLots.length}</p>
              <p className="text-sm text-gray-500">Mes lots</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Gavel className="w-8 h-8 mx-auto text-green-600 mb-2" />
              <p className="text-2xl font-bold">
                {mesLots.filter(l => l.statut === 'ouvert').length}
              </p>
              <p className="text-sm text-gray-500">En cours</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
              <p className="text-2xl font-bold">
                {mesLots.filter(l => l.statut === 'attribue').length}
              </p>
              <p className="text-sm text-gray-500">Attribués</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Lock className="w-8 h-8 mx-auto text-orange-600 mb-2" />
              <p className="text-2xl font-bold">
                {mesLots.filter(l => l.statut === 'clos').length}
              </p>
              <p className="text-sm text-gray-500">À attribuer</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="lots" className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList className="bg-white border shadow-sm">
              <TabsTrigger value="lots" className="data-[state=active]:bg-[#F0A22E] data-[state=active]:text-white">
                <Package className="w-4 h-4 mr-2" />
                Mes lots
              </TabsTrigger>
              <TabsTrigger value="attribuer" className="data-[state=active]:bg-[#F0A22E] data-[state=active]:text-white">
                <Award className="w-4 h-4 mr-2" />
                À attribuer
              </TabsTrigger>
            </TabsList>

            <Button onClick={() => setShowCreateLotModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouveau lot
            </Button>
          </div>

          {/* Mes lots Tab */}
          <TabsContent value="lots" className="space-y-4">
            {mesLots.length === 0 ? (
              <Card className="bg-gray-50 border-dashed">
                <CardContent className="py-12 text-center">
                  <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucun lot</h3>
                  <p className="text-gray-500 mb-4">
                    Créez votre premier lot pour le mettre en vente.
                  </p>
                  <Button onClick={() => setShowCreateLotModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un lot
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {mesLots.map(lot => {
                  const statusConfig = getStatusConfig(lot.statut);
                  const StatusIcon = statusConfig.icon;

                  return (
                    <Card key={lot.id} className="overflow-hidden">
                      <CardContent className="p-6">
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-start gap-3">
                              <Badge className={statusConfig.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                              <Badge variant="outline">Lot {lot.numero_lot}</Badge>
                            </div>

                            <h3 className="text-xl font-bold text-[#0A2540] mt-3">
                              {lot.titre_fr}
                            </h3>

                            {lot.commune && (
                              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                                <MapPin className="w-4 h-4" />
                                {lot.commune}{lot.departement ? ` (${lot.departement})` : ''}
                              </p>
                            )}

                            <p className="text-gray-600 mt-2 line-clamp-2">
                              {lot.description_vendeur_fr}
                            </p>

                            {lot.vacation_titre && (
                              <p className="text-sm mt-3">
                                <span className="text-gray-500">Vente : </span>
                                <Link
                                  to={`/encheres/${lot.vacation_slug}`}
                                  className="text-[#F0A22E] hover:underline"
                                >
                                  {lot.vacation_titre}
                                </Link>
                              </p>
                            )}
                          </div>

                          <div className="lg:text-right space-y-2">
                            <p className="text-sm text-gray-500">Mise à prix</p>
                            <p className="text-2xl font-bold text-[#0A2540]">
                              {formatPrice(lot.mise_a_prix)} €
                            </p>

                            {lot.meilleure_offre && (
                              <div className="mt-2 p-2 bg-[#F0A22E]/10 rounded">
                                <p className="text-sm text-gray-600">Meilleure offre</p>
                                <p className="text-xl font-bold text-[#F0A22E]">
                                  {formatPrice(lot.meilleure_offre)} €
                                </p>
                                <p className="text-xs text-gray-500">{lot.nb_offres} offre(s)</p>
                              </div>
                            )}

                            <div className="flex gap-2 mt-4 justify-end">
                              {lot.statut === 'clos' && (
                                <Button
                                  variant="default"
                                  onClick={() => fetchOffresForLot(lot)}
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  Voir les offres
                                </Button>
                              )}
                              {['a_venir', 'ouvert'].includes(lot.statut) && lot.vacation_statut !== 'vente_en_cours' && (
                                <Button variant="outline" size="sm">
                                  <Edit className="w-4 h-4 mr-2" />
                                  Modifier
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* À attribuer Tab */}
          <TabsContent value="attribuer" className="space-y-4">
            {mesLots.filter(l => l.statut === 'clos').length === 0 ? (
              <Card className="bg-gray-50 border-dashed">
                <CardContent className="py-12 text-center">
                  <Award className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    Aucun lot à attribuer
                  </h3>
                  <p className="text-gray-500">
                    Les lots clos apparaîtront ici pour que vous puissiez choisir l&apos;acquéreur.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {mesLots.filter(l => l.statut === 'clos').map(lot => (
                  <Card key={lot.id} className="border-orange-200 bg-orange-50/30">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <Badge className="bg-orange-100 text-orange-700 mb-2">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Attribution en attente
                          </Badge>
                          <h3 className="text-lg font-bold text-[#0A2540]">{lot.titre_fr}</h3>
                          <p className="text-sm text-gray-500">{lot.nb_offres || 0} offre(s) reçue(s)</p>
                        </div>
                        <Button onClick={() => fetchOffresForLot(lot)}>
                          <Eye className="w-4 h-4 mr-2" />
                          Voir les offres et attribuer
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Info Section */}
        <Card className="mt-8 bg-[#0A2540] text-white">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-8 h-8 text-[#F0A22E] flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg mb-2">Comment fonctionne la vente ?</h3>
                <ul className="text-gray-300 space-y-2 text-sm">
                  <li>1. Créez vos lots avec <strong>photos</strong>, description détaillée et mise à prix.</li>
                  <li>2. Les acheteurs inscrits et validés peuvent enchérir pendant la période de vente.</li>
                  <li>3. Après la clôture, vous recevez toutes les offres avec l&apos;identité des acheteurs.</li>
                  <li>4. <strong>Vous restez libre du choix de l&apos;acquéreur</strong> - vous pouvez refuser toutes les offres.</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Lot Modal */}
      <Dialog open={showCreateLotModal} onOpenChange={(open) => {
        if (!open) resetForm();
        setShowCreateLotModal(open);
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-[#F0A22E]" />
              Créer un nouveau lot
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Vacation Selection */}
            <div>
              <Label className="text-sm font-semibold">Vente concernée *</Label>
              <Select
                value={newLot.vacation_id}
                onValueChange={(v) => setNewLot({ ...newLot, vacation_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez une vente" />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  {vacationsDisponibles.length === 0 ? (
                    <div className="p-2 text-sm text-gray-500">Aucune vente disponible</div>
                  ) : (
                    vacationsDisponibles.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.titre_fr}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div>
              <Label className="text-sm font-semibold">Titre du lot *</Label>
              <Input
                value={newLot.titre_fr}
                onChange={(e) => setNewLot({ ...newLot, titre_fr: e.target.value })}
                placeholder="Ex: Foreuse Comacchio MC600 - 2018"
              />
            </div>

            {/* 📸 PHOTOS SECTION */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center gap-2 mb-3">
                <Camera className="w-5 h-5 text-[#F0A22E]" />
                <Label className="text-sm font-semibold">Photos du matériel *</Label>
              </div>
              
              <input
                ref={photoInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handlePhotoSelect}
                className="hidden"
              />
              
              <div className="grid grid-cols-4 gap-3">
                {selectedPhotos.map((photo, index) => (
                  <div
                    key={index}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                      index === mainPhotoIndex 
                        ? 'border-[#F0A22E] ring-2 ring-[#F0A22E]/30' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setMainPhotoIndex(index)}
                  >
                    <img
                      src={photo.preview}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {index === mainPhotoIndex && (
                      <div className="absolute top-1 left-1 bg-[#F0A22E] text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1">
                        <Star className="w-3 h-3" /> Principale
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removePhoto(index); }}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                
                {/* Add photo button */}
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-[#F0A22E] hover:bg-[#F0A22E]/5 flex flex-col items-center justify-center gap-1 transition-colors"
                >
                  <Plus className="w-6 h-6 text-gray-400" />
                  <span className="text-xs text-gray-500">Ajouter</span>
                </button>
              </div>
              
              <p className="text-xs text-gray-500 mt-2">
                Cliquez sur une photo pour la définir comme principale. Formats: JPG, PNG, WebP.
              </p>
            </div>

            {/* 🔧 STRUCTURED FIELDS */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center gap-2 mb-3">
                <Wrench className="w-5 h-5 text-[#F0A22E]" />
                <Label className="text-sm font-semibold">Caractéristiques techniques</Label>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-600">Marque</Label>
                  <Input
                    value={newLot.marque}
                    onChange={(e) => setNewLot({ ...newLot, marque: e.target.value })}
                    placeholder="Ex: Comacchio"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Modèle</Label>
                  <Input
                    value={newLot.modele}
                    onChange={(e) => setNewLot({ ...newLot, modele: e.target.value })}
                    placeholder="Ex: MC600"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Année de mise en service</Label>
                  <Input
                    type="number"
                    value={newLot.annee_mise_service}
                    onChange={(e) => setNewLot({ ...newLot, annee_mise_service: e.target.value })}
                    placeholder="Ex: 2018"
                    min="1980"
                    max={new Date().getFullYear()}
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Heures compteur</Label>
                  <Input
                    type="number"
                    value={newLot.heures_compteur}
                    onChange={(e) => setNewLot({ ...newLot, heures_compteur: e.target.value })}
                    placeholder="Ex: 3500"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-gray-600">État général</Label>
                  <Select
                    value={newLot.etat_general}
                    onValueChange={(v) => setNewLot({ ...newLot, etat_general: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez l'état" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excellent">Excellent - Comme neuf</SelectItem>
                      <SelectItem value="bon">Bon - Usure normale</SelectItem>
                      <SelectItem value="correct">Correct - Fonctionnel</SelectItem>
                      <SelectItem value="a_reviser">À réviser</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* 📝 DESCRIPTION */}
            <div>
              <Label className="text-sm font-semibold">Description détaillée *</Label>
              <Textarea
                value={newLot.description_vendeur_fr}
                onChange={(e) => setNewLot({ ...newLot, description_vendeur_fr: e.target.value })}
                placeholder="Décrivez votre matériel en détail : historique, entretien, équipements inclus, raison de la vente..."
                rows={5}
              />
              <p className="text-xs text-gray-500 mt-1">
                Cette description est rédigée par vous seul et ne sera pas modifiée par GEOTS.
              </p>
            </div>

            {/* 📎 DOCUMENTS SECTION */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-[#F0A22E]" />
                <Label className="text-sm font-semibold">Documents (PDF)</Label>
              </div>
              
              <input
                ref={docInputRef}
                type="file"
                multiple
                accept=".pdf"
                onChange={handleDocSelect}
                className="hidden"
              />
              
              {selectedDocs.length > 0 && (
                <div className="space-y-2 mb-3">
                  {selectedDocs.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                      <div className="flex items-center gap-2">
                        <File className="w-4 h-4 text-red-500" />
                        <span className="text-sm truncate max-w-xs">{doc.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDoc(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <Button
                type="button"
                variant="outline"
                onClick={() => docInputRef.current?.click()}
                className="w-full"
              >
                <Upload className="w-4 h-4 mr-2" />
                Ajouter fiche technique, carnet d&apos;entretien, certificats...
              </Button>
            </div>

            {/* 📍 LOCATION */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold">Commune</Label>
                <Input
                  value={newLot.commune}
                  onChange={(e) => setNewLot({ ...newLot, commune: e.target.value })}
                  placeholder="Paris"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">Département</Label>
                <Input
                  value={newLot.departement}
                  onChange={(e) => setNewLot({ ...newLot, departement: e.target.value })}
                  placeholder="75"
                />
              </div>
            </div>

            {/* 💰 PRICE */}
            <div className="border rounded-lg p-4 bg-[#F0A22E]/5">
              <div className="flex items-center gap-2 mb-3">
                <Euro className="w-5 h-5 text-[#F0A22E]" />
                <Label className="text-sm font-semibold">Prix</Label>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-600">Mise à prix (€) *</Label>
                  <Input
                    type="number"
                    value={newLot.mise_a_prix}
                    onChange={(e) => setNewLot({ ...newLot, mise_a_prix: parseInt(e.target.value) || 0 })}
                    className="text-lg font-semibold"
                  />
                  <p className="text-xs text-gray-500 mt-1">Commission incluse</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Pas d&apos;enchère (€)</Label>
                  <Input
                    type="number"
                    value={newLot.pas_enchere}
                    onChange={(e) => setNewLot({ ...newLot, pas_enchere: parseInt(e.target.value) || 100 })}
                  />
                </div>
              </div>
            </div>

            {/* Mode de visite */}
            <div>
              <Label className="text-sm font-semibold">Mode de visite</Label>
              <Select
                value={newLot.mode_visite}
                onValueChange={(v) => setNewLot({ ...newLot, mode_visite: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="facultative">Facultative</SelectItem>
                  <SelectItem value="obligatoire">Obligatoire</SelectItem>
                  <SelectItem value="sans_visite">Sans visite</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCreateLotModal(false)} disabled={creatingLot}>
              Annuler
            </Button>
            <Button onClick={handleCreateLot} disabled={creatingLot}>
              {creatingLot ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Création en cours...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer le lot
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offres Modal */}
      <Dialog open={showOffresModal} onOpenChange={setShowOffresModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Offres pour : {selectedLot?.titre_fr}
            </DialogTitle>
          </DialogHeader>

          {offresLot && (
            <div className="space-y-4">
              {!offresLot.can_see_identities ? (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-yellow-800">Enchères en cours</h4>
                      <p className="text-sm text-yellow-700">
                        Les identités des enchérisseurs seront révélées après la clôture de la vente.
                      </p>
                      <div className="mt-3 flex gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Nombre d&apos;offres</p>
                          <p className="text-xl font-bold text-[#0A2540]">{offresLot.nb_offres}</p>
                        </div>
                        {offresLot.meilleure_offre && (
                          <div>
                            <p className="text-xs text-gray-500">Meilleure offre</p>
                            <p className="text-xl font-bold text-[#F0A22E]">
                              {formatPrice(offresLot.meilleure_offre)} €
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200 mb-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-green-800">
                          Enchères closes - {offresLot.nb_offres} offre(s)
                        </h4>
                        <p className="text-sm text-green-700">
                          Vous pouvez maintenant voir les identités et choisir l&apos;acquéreur.
                        </p>
                      </div>
                    </div>
                  </div>

                  {offresLot.offres.length === 0 ? (
                    <div className="text-center py-8">
                      <Gavel className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">Aucune offre reçue pour ce lot.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {offresLot.offres.map((offre, idx) => (
                        <Card
                          key={offre.offre_id}
                          className={`cursor-pointer transition-all ${
                            attributionData.inscriptionRetenue === offre.inscription_id
                              ? 'border-[#F0A22E] bg-[#F0A22E]/5'
                              : 'hover:border-gray-300'
                          }`}
                          onClick={() => setAttributionData({
                            ...attributionData,
                            inscriptionRetenue: offre.inscription_id
                          })}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  {idx === 0 && (
                                    <Badge className="bg-[#F0A22E] text-white">
                                      Meilleure offre
                                    </Badge>
                                  )}
                                  <Badge variant="outline">#{idx + 1}</Badge>
                                </div>
                                <p className="font-bold text-[#0A2540]">{offre.entreprise}</p>
                                <p className="text-sm text-gray-500">SIRET: {offre.siret}</p>
                                <p className="text-sm text-gray-600">{offre.contact_nom}</p>
                                <p className="text-sm text-gray-500">{offre.contact_email}</p>
                                {offre.projet && (
                                  <p className="text-sm mt-2 text-gray-600">
                                    <span className="font-medium">Projet :</span> {offre.projet}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-[#0A2540]">
                                  {formatPrice(offre.montant)} €
                                </p>
                                <p className="text-xs text-gray-500">
                                  {offre.financement || 'Non précisé'}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}

                  <div className="border-t pt-4 mt-4">
                    <Label>Motif de décision (optionnel)</Label>
                    <Textarea
                      value={attributionData.motif}
                      onChange={(e) => setAttributionData({ ...attributionData, motif: e.target.value })}
                      placeholder="Raison de votre choix..."
                      rows={2}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowOffresModal(false)}>
              Fermer
            </Button>
            {offresLot?.can_see_identities && (
              <>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setAttributionData({ ...attributionData, inscriptionRetenue: null });
                    handleAttribution();
                  }}
                >
                  Refuser toutes les offres
                </Button>
                <Button
                  onClick={handleAttribution}
                  disabled={!attributionData.inscriptionRetenue}
                >
                  <Award className="w-4 h-4 mr-2" />
                  Attribuer à la sélection
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
